import { useState, useRef, useEffect } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { LogIn, LogOut, ChevronDown, X } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { useAppStore } from '@/store/appStore'
import { LoginForm } from '@/components/auth/LoginForm'

/**
 * Top-right account / sign-in entry for the homepage (ContextSelector hero).
 *
 * It is the login ENTRY POINT surfaced inline instead of only as the full-screen
 * LoginPage wall:
 *   - Signed in as a real user (a Supabase session in any non-demo posture) →
 *     an account chip with initials + name, dropdown → Sign out.
 *   - Otherwise (demo, or no session) → a "Sign in" button that opens the same
 *     LoginForm in a modal. In demo, signing in upgrades you to a real session;
 *     the persona switcher remains the in-demo identity affordance.
 *
 * Styled for the dark hero background (light text, translucent surfaces).
 */
export function AccountMenu() {
  const session = useAuthStore((s) => s.session)
  const user = useAuthStore((s) => s.user)
  const signOut = useAuthStore((s) => s.signOut)
  const appMode = useAppStore((s) => s.appMode)
  const currentUser = useAppStore((s) => s.currentUser)

  const [menuOpen, setMenuOpen] = useState(false)
  const [loginOpen, setLoginOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [menuOpen])

  // Demo is a showcase, not an authenticated posture. A "Sign in" entry here is a
  // production concept leaking into the demo — it confuses the walkthrough (there's
  // no account to sign into) and competes with the real in-demo identity mechanism
  // (persona / act-as). So hide the account control entirely in demo; real sign-in
  // belongs to Sandbox / Production, where the environment actually has accounts.
  if (appMode === 'demo') return null

  // Past the demo gate, any Supabase session is a real authenticated user.
  const realAuth = !!session

  const email = user?.email ?? ''
  const name = currentUser?.name || (user?.user_metadata?.full_name as string | undefined) || email || 'Account'
  const initials =
    currentUser?.initials ||
    name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase() ||
    'AC'

  async function handleSignOut() {
    setMenuOpen(false)
    await signOut()
  }

  if (realAuth) {
    return (
      <div ref={ref} className="relative">
        <button
          onClick={() => setMenuOpen((v) => !v)}
          className="inline-flex items-center gap-2 h-9 pl-1.5 pr-2.5 rounded-full border border-white/25 bg-white/10 text-white hover:bg-white/20 active:scale-95 transition-colors"
          title="Account"
        >
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white/90 text-brand-800 text-[11px] font-bold">
            {initials}
          </span>
          <span className="hidden sm:inline max-w-[140px] truncate text-body-sm font-medium">{name}</span>
          <ChevronDown size={14} className="opacity-70" />
        </button>
        {menuOpen && (
          <div className="absolute right-0 top-full mt-1.5 w-56 bg-surface rounded-lg shadow-dropdown border border-border z-30 py-1 text-text-primary">
            <div className="px-3 py-2 border-b border-border">
              <div className="text-body-sm font-semibold truncate">{name}</div>
              {email && <div className="text-[11px] text-text-tertiary truncate">{email}</div>}
            </div>
            <button
              onClick={handleSignOut}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-body-sm hover:bg-surface-muted transition-colors"
            >
              <LogOut size={14} className="flex-shrink-0 text-text-tertiary" />
              Sign out
            </button>
          </div>
        )}
      </div>
    )
  }

  return (
    <Dialog.Root open={loginOpen} onOpenChange={setLoginOpen}>
      <Dialog.Trigger asChild>
        <button
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-white/25 bg-white/10 text-white hover:bg-white/20 active:scale-95 transition-colors"
          title="Sign in to your account"
        >
          <LogIn size={15} className="flex-shrink-0" />
          <span className="text-body-sm font-medium">Sign in</span>
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border bg-white shadow-xl p-7 focus:outline-none">
          <div className="flex items-start justify-between mb-5">
            <div>
              <Dialog.Title className="text-heading-md font-display text-text-primary">Sign in</Dialog.Title>
              <Dialog.Description className="text-body-sm text-text-secondary mt-0.5">
                Sign in to your account to continue.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button className="text-text-tertiary hover:text-text-secondary p-1 -mr-1" aria-label="Close">
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>
          <LoginForm onSuccess={() => setLoginOpen(false)} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
